import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { motion } from "framer-motion";
import { CheckCircle, AlertTriangle, Droplets, RotateCw, Wrench, Play } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useLanguage } from "@/components/LanguageContext";

export default function HowToUsePage() {
  const { t } = useLanguage();

  const phase1Steps = [
    { icon: Wrench, color: "bg-red-100", iconColor: "text-red-600", hover: "hover:border-red-600", h: t("htu_p1_s1_h"), p: [<span key="a">{t("htu_p1_s1_p1")}<strong>{t("htu_p1_s1_p2")}</strong>{t("htu_p1_s1_p3")}</span>] },
    { icon: Droplets, color: "bg-blue-100", iconColor: "text-blue-600", hover: "hover:border-red-600", h: t("htu_p1_s2_h"), p: [<span key="a">{t("htu_p1_s2_p1")}<strong>{t("htu_p1_s2_p2")}</strong>{t("htu_p1_s2_p3")}</span>] },
    { icon: CheckCircle, color: "bg-green-100", iconColor: "text-green-600", hover: "hover:border-red-600", h: t("htu_p1_s3_h"), p: [<span key="a">{t("htu_p1_s3_p1")}<strong>{t("htu_p1_s3_p2")}</strong>{t("htu_p1_s3_p3")}<strong>{t("htu_p1_s3_p4")}</strong>{t("htu_p1_s3_p5")}</span>] },
    { num: "4", color: "bg-purple-100", iconColor: "text-purple-600", hover: "hover:border-red-600", h: t("htu_p1_s4_h"), p: [<span key="a">{t("htu_p1_s4_p1")}<strong>{t("htu_p1_s4_p2")}</strong>{t("htu_p1_s4_p3")}</span>] },
    { num: "5", color: "bg-orange-100", iconColor: "text-orange-600", hover: "hover:border-red-600", h: t("htu_p1_s5_h"), p: [<span key="a">{t("htu_p1_s5_p1")}<strong>{t("htu_p1_s5_p2")}</strong>{t("htu_p1_s5_p3")}</span>] },
    { icon: RotateCw, color: "bg-blue-100", iconColor: "text-blue-600", hover: "hover:border-red-600", h: t("htu_p1_s6_h"), p: [<span key="a">{t("htu_p1_s6_p1")}<strong>{t("htu_p1_s6_p2")}</strong>{t("htu_p1_s6_p3")}</span>] },
    { num: "7", color: "bg-green-100", iconColor: "text-green-600", hover: "hover:border-red-600", h: t("htu_p1_s7_h"), p: [t("htu_p1_s7_p")] },
  ];

  const phase2Steps = [
    { icon: Droplets, color: "bg-blue-100", iconColor: "text-blue-600", hover: "hover:border-green-600", h: t("htu_p2_s1_h"), p: [<span key="a">{t("htu_p2_s1_p1")}<strong>{t("htu_p2_s1_p2")}</strong>{t("htu_p2_s1_p3")}</span>] },
    { num: "2", color: "bg-purple-100", iconColor: "text-purple-600", hover: "hover:border-green-600", h: t("htu_p2_s2_h"), p: [t("htu_p2_s2_p")] },
    { icon: CheckCircle, color: "bg-green-100", iconColor: "text-green-600", hover: "hover:border-green-600", h: t("htu_p2_s3_h"), p: [<span key="a">{t("htu_p2_s3_p1")}<strong>{t("htu_p2_s3_p2")}</strong>{t("htu_p2_s3_p3")}</span>] },
    { num: "4", color: "bg-orange-100", iconColor: "text-orange-600", hover: "hover:border-green-600", h: t("htu_p2_s4_h"), p: [<span key="a">{t("htu_p2_s4_p1")}<strong>{t("htu_p2_s4_p2")}</strong>{t("htu_p2_s4_p3")}</span>] },
    { icon: RotateCw, color: "bg-green-100", iconColor: "text-green-600", hover: "hover:border-green-600", h: t("htu_p2_s5_h"), p: [<span key="a">{t("htu_p2_s5_p1")}<strong>{t("htu_p2_s5_p2")}</strong>{t("htu_p2_s5_p3")}</span>] },
  ];

  const safetyTips = [
    [t("htu_safety_1_1"), t("htu_safety_1_2")],
    [t("htu_safety_2_1"), t("htu_safety_2_2")],
    [t("htu_safety_3_1"), t("htu_safety_3_2")],
    [t("htu_safety_4_1"), t("htu_safety_4_2")],
    [t("htu_safety_5_1"), t("htu_safety_5_2")],
  ];

  const renderStep = (step, idx) => (
    <Card key={idx} className={`border-2 border-gray-200 ${step.hover} transition-all`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-10 h-10 ${step.color} rounded-lg flex items-center justify-center`}>
            {step.icon ? (
              <step.icon className={`w-5 h-5 ${step.iconColor}`} />
            ) : (
              <span className={`${step.iconColor} font-black`}>{step.num}</span>
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{step.h}</h3>
            <p className="text-gray-700 leading-relaxed">{step.p}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="bg-white min-h-screen">
      {/* SEO Optimized H1 */}
      <h1 className="sr-only">{t("htu_srh1")}</h1>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-sm font-bold">{t("htu_hero_badge")}</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-black mb-6">{t("htu_hero_title")}</h2>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">{t("htu_hero_sub")}</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
            {t("htu_intro_p1")}<strong>{t("htu_intro_p2")}</strong>{t("htu_intro_p3")}
          </p>

          <Alert className="bg-blue-50 border-blue-300 border-2">
            <AlertTriangle className="h-5 w-5 text-blue-600" />
            <AlertDescription className="text-blue-900 font-semibold">
              <strong>{t("htu_alert_strong")}</strong>{t("htu_alert_text")}
            </AlertDescription>
          </Alert>
        </motion.div>

        {/* Official Video Tutorial */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <Card className="border-2 border-blue-200 shadow-xl overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
                <div className="flex items-center gap-3">
                  <Play className="w-8 h-8" />
                  <div>
                    <h3 className="text-2xl font-black">{t("htu_video_h")}</h3>
                    <p className="text-blue-100">{t("htu_video_sub")}</p>
                  </div>
                </div>
              </div>
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/ejXbB-KdL34"
                  title="LCTX Tire Changer - Complete Usage Tutorial"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Phase 1 */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
              <span className="text-white text-2xl font-black">1</span>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900">{t("htu_phase1_title")}</h2>
              <p className="text-gray-600">{t("htu_phase1_sub")}</p>
            </div>
          </div>
          <div className="space-y-6">
            {phase1Steps.map((step, idx) => renderStep(step, idx))}
          </div>
        </motion.section>

        {/* Phase 2 */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
              <span className="text-white text-2xl font-black">2</span>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900">{t("htu_phase2_title")}</h2>
              <p className="text-gray-600">{t("htu_phase2_sub")}</p>
            </div>
          </div>
          <div className="space-y-6">
            {phase2Steps.map((step, idx) => renderStep(step, idx))}
          </div>
        </motion.section>

        {/* Safety Tips */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-300">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-10 h-10 text-red-600 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-black text-red-900 mb-4">{t("htu_safety_h")}</h2>
                  <ul className="space-y-3 text-gray-800">
                    {safetyTips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-red-600 font-bold">•</span>
                        <span><strong>{tip[0]}</strong>{tip[1]}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Card className="bg-gradient-to-r from-blue-600 to-blue-700 border-0 shadow-2xl">
            <CardContent className="p-8 md:p-12 text-center text-white">
              <h2 className="text-3xl md:text-4xl font-black mb-4">{t("htu_cta_h")}</h2>
              <p className="text-xl mb-8 text-blue-100">{t("htu_cta_sub")}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to={createPageUrl("Product")}>
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 h-14 px-10 text-lg font-bold">
                    {t("htu_cta_btn1")}
                  </Button>
                </Link>
                <a
                  href="https://www.youtube.com/@Eztirechanger?sub_confirmation=1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="lg" variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 h-14 px-10 text-lg font-bold">
                    <Play className="w-5 h-5 mr-2" />
                    {t("htu_cta_btn2")}
                  </Button>
                </a>
              </div>
              <p className="mt-6 text-blue-100">{t("htu_cta_footer")}</p>
            </CardContent>
          </Card>
        </motion.section>
      </div>
    </div>
  );
}